import { useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { dispatch } from 'store'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import { aiAvatarThunks } from 'store/features/aiAvatar/thunks'
import { IMyFilesMediaItem } from 'types/media'
import { IUserAudio } from 'store/types/audio'
import { useFileFormatValidator } from 'hooks/useFileFormatValidator'
import { openMediaLib } from 'utils/media'
import {
  getItemFromLocalStorage,
  setItemInLocalStorage
} from 'utils/localStorage'
import { VOICE_CLONE_CONSENT_KEY } from './consts'

export const useVoiceClone = () => {
  const clonedVoice = useSelector(aiAvatarSelectors.clonedVoice)
  const uploadStatus = useSelector(aiAvatarSelectors.voiceCloneUploadStatus)

  const uploadTriggerRef = useRef<{ triggerUpload: () => void } | null>(null)
  const validateFiles = useFileFormatValidator('audio')
  // Files waiting behind the one-time consent popup; non-null = popup open.
  const [pendingConsentFiles, setPendingConsentFiles] = useState<File[] | null>(
    null
  )

  const uploadFirstValidFile = useCallback(
    (files: File[]) => {
      const [file] = validateFiles(files)
      if (file) {
        dispatch(aiAvatarThunks.uploadVoiceCloneFile(file))
      }
    },
    [validateFiles]
  )

  // Entry point for both the file-input and drag & drop. Before the very first
  // upload the user must confirm they own the rights to the voice; the consent
  // is one-time (persisted), so subsequent uploads start right away.
  const handleFilesUpload = useCallback(
    async (files: File[] | FileList) => {
      // Copy before the file input is reset — its FileList is live.
      const fileArray = Array.from(files)

      if (getItemFromLocalStorage(VOICE_CLONE_CONSENT_KEY)) {
        uploadFirstValidFile(fileArray)
        return
      }

      setPendingConsentFiles(fileArray)
    },
    [uploadFirstValidFile]
  )

  const onConsentConfirm = useCallback(() => {
    setItemInLocalStorage(VOICE_CLONE_CONSENT_KEY, true)
    if (pendingConsentFiles) {
      uploadFirstValidFile(pendingConsentFiles)
    }
    setPendingConsentFiles(null)
  }, [pendingConsentFiles, uploadFirstValidFile])

  const onConsentCancel = useCallback(() => {
    setPendingConsentFiles(null)
  }, [])

  const onUploadClick = useCallback(() => {
    uploadTriggerRef.current?.triggerUpload()
  }, [])

  const onMyVoicesClick = useCallback(() => {
    openMediaLib({
      fileType: 'audio',
      // Open the media library on the Sounds folder, preselecting its
      // Voice clone sub-tab, where the user's voice files live.
      initialFolderName: 'sounds',
      initialSoundFileType: 'voiceClone',
      maxFileImportCount: 1,
      onChoose: (files: IMyFilesMediaItem[]) => {
        // For fileType 'audio' the media library returns sound items in the
        // usersounds shape (`title`/`path`), not IMyFilesMediaItem.
        const [item] = files as unknown as IUserAudio[]
        if (item) {
          dispatch(aiAvatarThunks.selectClonedVoiceFromMyFiles(item))
        }
      }
    })
  }, [])

  const onRemoveVoice = useCallback(() => {
    dispatch(aiAvatarThunks.removeClonedVoice())
  }, [])

  const onTryAgain = useCallback(() => {
    dispatch(aiAvatarThunks.setVoiceCloneUploadStatus('idle'))
    uploadTriggerRef.current?.triggerUpload()
  }, [])

  const onFailedPopupClose = useCallback(() => {
    dispatch(aiAvatarThunks.setVoiceCloneUploadStatus('idle'))
  }, [])

  return {
    clonedVoice,
    uploadStatus,
    uploadTriggerRef,
    isConsentOpen: !!pendingConsentFiles,
    handleFilesUpload,
    onConsentConfirm,
    onConsentCancel,
    onUploadClick,
    onMyVoicesClick,
    onRemoveVoice,
    onTryAgain,
    onFailedPopupClose
  }
}
